import { type JSX, useCallback, useEffect, useState } from 'react';

import { SessionApiError } from '../../session/client';
import {
  type CounterSpendRequest,
  approveCounterSpend,
  counterSpendCode,
  counterSpends,
  declineCounterSpend,
} from './rewards-api';

/**
 * PENNY at a counter (Rewards Card stage W6): a pharmacy's counter asked to
 * use some of this person's PENNY on a sale. Nothing is spent until they
 * approve it here. Or, if staff would rather, they read out the one-time code
 * shown here. The request names the business, so a request from somewhere they
 * are not is easy to refuse.
 *
 * Absent when there is nothing to approve (or the card is switched off): no
 * heading, nothing drawn.
 */

const money = (minor: number) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(minor / 100);
const timeOf = (iso: string) =>
  new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
const messageOf = (error: unknown) =>
  error instanceof SessionApiError ? error.message : 'Something went wrong. Try again.';

export function CounterApprovals({
  flash,
}: {
  readonly flash: (message: string) => void;
}): JSX.Element | null {
  const [requests, setRequests] = useState<readonly CounterSpendRequest[]>([]);
  const [codes, setCodes] = useState<Readonly<Record<string, string>>>({});
  const [busy, setBusy] = useState<string | undefined>();
  const load = useCallback(() => {
    counterSpends()
      .then(setRequests)
      .catch(() => setRequests([]));
  }, []);
  useEffect(() => {
    load();
    // A counter is waiting: look again every few seconds while the page is open.
    const timer = setInterval(load, 5000);
    return () => clearInterval(timer);
  }, [load]);

  const act = (id: string, work: () => Promise<unknown>, done: string) => {
    setBusy(id);
    work()
      .then(() => {
        flash(done);
        load();
      })
      .catch((error: unknown) => flash(messageOf(error)))
      .finally(() => setBusy(undefined));
  };

  if (requests.length === 0) return null;
  return (
    <section aria-label="PENNY at the counter" className="jt-rw-card jt-ca">
      <span className="jt-rw-h2">PENNY at the counter</span>
      {requests.map((r) => {
        const at = r.businessName ?? 'A pharmacy';
        const code = codes[r.id];
        return (
          <div key={r.id} className="jt-ca-request" aria-label={`Request from ${at}`} role="group">
            {r.status === 'APPROVED' ? (
              <span className="jt-rw-copy" role="status">
                <strong>Approved:</strong> {r.units} PENNY ({money(r.valueMinor)}) comes off at {at}. You pay{' '}
                {money(r.payableMinor)}.
              </span>
            ) : (
              <>
                <span className="jt-rw-copy">
                  <strong>
                    Use {r.units} PENNY ({money(r.valueMinor)}) at {at}?
                  </strong>{' '}
                  The sale is {money(r.totalMinor)}; you would pay {money(r.payableMinor)}. Approve by{' '}
                  {timeOf(r.expiresAt)}. If you are not at this counter, decline it.
                </span>
                <span className="jt-rc-actions">
                  <button
                    type="button"
                    className="jt-ord-pill jt-rc-show"
                    disabled={busy === r.id}
                    onClick={() =>
                      act(
                        r.id,
                        () => approveCounterSpend(r.id),
                        'Approved. Your PENNY is held for this sale.',
                      )
                    }
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    className="jt-ord-pill jt-ord-pill--soft"
                    disabled={busy === r.id}
                    onClick={() => act(r.id, () => declineCounterSpend(r.id), 'Declined. Nothing was spent.')}
                  >
                    Decline
                  </button>
                  <button
                    type="button"
                    className="jt-ord-pill jt-ord-pill--soft"
                    disabled={busy === r.id}
                    onClick={() => {
                      setBusy(r.id);
                      counterSpendCode(r.id)
                        .then((c) => setCodes({ ...codes, [r.id]: c.code }))
                        .catch((error: unknown) => flash(messageOf(error)))
                        .finally(() => setBusy(undefined));
                    }}
                  >
                    Show a code instead
                  </button>
                </span>
                {code !== undefined ? (
                  <span className="jt-rw-copy jt-ca-code" role="status">
                    Read this code to the staff: <strong className="jt-ca-digits">{code}</strong>. It works
                    once, for this sale only.
                  </span>
                ) : null}
              </>
            )}
          </div>
        );
      })}
    </section>
  );
}
